// Pure chart-option and projection builders for the debt-detail page. Kept free
// of Svelte/DOM so the null-guards, month-axis union, and payoff wiring can be
// unit-tested. The returned objects are Highcharts configs (Chart.svelte takes
// them as `unknown`).

import { formatCurrency } from './format';
import { projectPayoff, lastPaymentMagnitude, type PayoffProjection } from './debt';
import { DEBT_TYPES, type DebtView, type SeriesPoint } from './db';

export type ChartOptions = Record<string, unknown>;

export type Projections = {
	min: PayoffProjection | null;
	custom: PayoffProjection | null;
} | null;

/**
 * The "what-if" payment seed: the last payment's magnitude when it exceeds the
 * minimum, otherwise the minimum. 0 when there's no debt.
 */
export function customPaymentDefault(debt: DebtView | null): number {
	if (!debt) return 0;
	const last = lastPaymentMagnitude(debt.payments);
	const min = debt.minimumPayment ?? 0;
	return last > min ? last : min;
}

/**
 * Forward payoff projections at the minimum and at `effectiveCustom`, for an
 * interest-free facility whose balance is still above target. `now` is
 * injectable for testing. Returns null when projection doesn't apply.
 */
export function computeProjections(
	debt: DebtView | null,
	effectiveCustom: number,
	now: Date
): Projections {
	if (!debt || debt.debtType !== DEBT_TYPES.INTEREST_FREE_FACILITY) return null;
	const current = debt.currentBalance ?? 0;
	const target = debt.targetBalance ?? 0;
	if (current <= target) return null;
	const freq = debt.paymentFrequency ?? 'monthly';
	const minPay = debt.minimumPayment ?? 0;
	return {
		min: minPay > 0 ? projectPayoff(current, minPay, freq, now, target) : null,
		custom: effectiveCustom > 0 ? projectPayoff(current, effectiveCustom, freq, now, target) : null,
	};
}

export function formatMonthLabel(ym: string): string {
	const [y, m] = ym.split('-').map(Number);
	return new Date(y, m - 1, 1).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' });
}

export function payoffText(p: { payoffMonth: string | null; months: number } | null): string {
	if (!p) return '';
	if (!p.payoffMonth) return 'payment too small to pay it off';
	return `paid off ~${formatMonthLabel(p.payoffMonth)} (${p.months} mo)`;
}

/**
 * Balance chart for an interest-free facility: actual + expected to date, plus
 * the min/custom payoff projections. All series share a sorted union month axis
 * (null where a series has no point). Null when not a facility or no data.
 */
export function buildFacilityChartOptions(
	debt: DebtView | null,
	projections: Projections,
	effectiveCustom: number
): ChartOptions | null {
	if (!debt || debt.debtType !== DEBT_TYPES.INTEREST_FREE_FACILITY) return null;
	const expected = debt.expectedTrajectory ?? [];
	const actual = debt.trajectory ?? [];
	const minProj = projections?.min?.series ?? [];
	const customProj = projections?.custom?.series ?? [];
	if (expected.length === 0 && actual.length === 0 && minProj.length === 0) return null;

	const monthSet = new Set<string>();
	for (const list of [expected, actual, minProj, customProj]) {
		for (const p of list) monthSet.add(p.month);
	}
	const months = [...monthSet].sort();
	const onAxis = (pts: SeriesPoint[]) => {
		const m = new Map(pts.map((p) => [p.month, p.value]));
		return months.map((mm) => m.get(mm) ?? null);
	};

	const series: unknown[] = [
		{ name: 'Expected', data: onAxis(expected), color: '#94a3b8', dashStyle: 'ShortDash' },
		{ name: 'Actual', data: onAxis(actual), color: '#2563eb' },
	];
	if (minProj.length > 0) {
		series.push({ name: 'At minimum', data: onAxis(minProj), color: '#f59e0b', dashStyle: 'Dash' });
	}
	if (customProj.length > 0) {
		series.push({ name: `At ${formatCurrency(effectiveCustom)}`, data: onAxis(customProj), color: '#7c3aed', dashStyle: 'Dash' });
	}

	return {
		chart: { type: 'line' },
		title: { text: 'Balance: actual, expected & projected payoff' },
		credits: { enabled: false },
		xAxis: { categories: months },
		yAxis: {
			title: { text: 'NZD' },
			min: 0,
			plotLines: [
				{ value: debt.targetBalance ?? 0, color: '#10b981', dashStyle: 'Dash', width: 1, label: { text: 'Target' } },
			],
		},
		series,
	};
}

/** Interest & fees column chart, or null when there's no interest series. */
export function buildInterestOptions(debt: DebtView | null): ChartOptions | null {
	if (!debt || (debt.interestSeries?.length ?? 0) === 0) return null;
	return {
		chart: { type: 'column' },
		title: { text: 'Interest & fees over time' },
		credits: { enabled: false },
		legend: { enabled: false },
		xAxis: { categories: debt.interestSeries!.map((p) => p.month) },
		yAxis: { title: { text: 'NZD' }, min: 0 },
		series: [{ name: 'Interest', data: debt.interestSeries!.map((p) => p.value), color: '#ef4444' }],
	};
}

/** Legacy-balance trajectory line chart, or null when there's no trajectory. */
export function buildTrajectoryOptions(debt: DebtView | null): ChartOptions | null {
	if (!debt || (debt.trajectory?.length ?? 0) === 0) return null;
	return {
		chart: { type: 'line' },
		title: { text: 'Legacy balance trajectory' },
		credits: { enabled: false },
		legend: { enabled: false },
		xAxis: { categories: debt.trajectory!.map((p) => p.month) },
		yAxis: {
			title: { text: 'NZD' },
			min: 0,
			plotLines: [
				{ value: debt.targetBalance ?? 0, color: '#10b981', dashStyle: 'Dash', width: 1, label: { text: 'Target' } },
			],
		},
		series: [{ name: 'Legacy remaining', data: debt.trajectory!.map((p) => p.value), color: '#2563eb' }],
	};
}
